import type { MetaFunction } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
} from '@remix-run/server-runtime'
import { json, redirect } from '@remix-run/server-runtime'

import { InnerPageTopNav } from '~/components/inner-page-top-nav'
import { Seo } from '~/components/Seo'
import { formAction } from '~/helpers/form-action.server'
import { makeShopPath, makeMainPath } from '~/helpers/routes'
import { listCollectionsOfProduct } from '~/models/collection.server'
import { recordVisit } from '~/models/event.server'
import { listSimilarFullProducts } from '~/models/product.server'
import type { UpdateProductEnv } from '~/mutations/product'
import {
  archiveProductMutation,
  patchProductMutation,
  restoreProductMutation,
} from '~/mutations/product'
import { ProductPage } from '~/routes/shop.$shopSlug.products.$productId.$productSlug._index/components/product-page'
import {
  archiveProductSchema,
  patchProductSchema,
  restoreProductSchema,
} from '~/schemas/product'
import {
  requireProduct,
  requireLiveFullProduct,
} from '~/services/auth-product.server'
import {
  requireShopAndItsOptionalSeller,
  requireShopAndItsSeller,
} from '~/services/auth-shop.server'
import { setActionFromIntent } from '~/services/history.server'
import { getShopSession } from '~/services/session-shop.server'
import { SEO_IMAGE_OPTIONS } from '~/utils/constants'
import { getImageKitUrl, generateShopLogoFormats } from '~/utils/image'
import {
  generateProductSeoData,
  generateSeoImage,
  generateSeoMetaTags,
} from '~/utils/seo-helpers'

export const loader = async (loaderArgs: LoaderFunctionArgs) => {
  const { shop, seller } = await requireShopAndItsOptionalSeller(
    loaderArgs.request
  )
  const productId = loaderArgs.params.productId!

  if (seller) {
    return redirect(`${loaderArgs.request.url}/edit`)
  }

  const [product, similarProducts, collections] = await Promise.all([
    requireLiveFullProduct(shop, loaderArgs),
    shop.crossSellingEnabledAt
      ? listSimilarFullProducts(productId, { limit: 5 })
      : undefined,
    listCollectionsOfProduct(productId),
    recordVisit(productId, loaderArgs.request.url),
  ])

  if (product.archivedAt != null) {
    throw new Response('Forbidden', { status: 403 })
  }

  // check if product has any visible variants
  if (
    product.variants.filter((v) => v.isVisible && !v.archivedAt).length === 0
  ) {
    throw new Response('Not Found', { status: 404 })
  }

  const shopSession = await getShopSession(loaderArgs.request)
  const buyerCountry = shopSession.get('buyerCountry')

  // Generate product SEO data server-side
  const productSeoData = generateProductSeoData(product, shop)

  const firstImageAttachment = product.attachments.find(
    (att) => att.media.kind === 'image'
  )

  const productSeoImage = firstImageAttachment?.media?.src
    ? getImageKitUrl(firstImageAttachment.media.src).startsWith('http')
      ? generateSeoImage(firstImageAttachment.media.src, SEO_IMAGE_OPTIONS)
      : `${process.env.APP_URL}${generateSeoImage(firstImageAttachment.media.src, SEO_IMAGE_OPTIONS)}`
    : `/gradient.png`

  const canonicalUrl = makeShopPath(
    shop,
    `/products/${product.id}/${product.slug}`
  )

  // Generate shop favicon from theme color
  const shopLogos = await generateShopLogoFormats(shop.themeColor)
  const shopFaviconPng = shopLogos.png || makeMainPath(`/favicon.png`)
  const shopFaviconIco = shopLogos.ico || makeMainPath(`/favicon.ico`)

  return json({
    title: product.name,
    product,
    similarProducts,
    collections,
    buyerCountry,
    shop,
    productSeoData,
    seoInfo: {
      canonicalUrl,
      productSeoImage,
      shopFaviconPng,
      shopFaviconIco,
    },
  })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.product) {
    return [
      { title: 'Product Not Found' },
      {
        name: 'description',
        content: 'The requested product could not be found.',
      },
    ]
  }

  const { product, shop, seoInfo } = data

  const description =
    product.descriptionPrimary ||
    product.descriptionSecondary ||
    `${product.name} product`

  return generateSeoMetaTags(
    {
      title: `${product.name} - ${shop.slug}`,
      seoTitle: product.name,
      description,
      image: seoInfo.productSeoImage,
      canonicalUrl: seoInfo.canonicalUrl,
      ogType: 'product',
      ogUrl: seoInfo.canonicalUrl,
      twitterImage: seoInfo.productSeoImage,
    },
    shop,
    seoInfo.shopFaviconPng,
    seoInfo.shopFaviconIco
  )
}

export async function action(actionArgs: ActionFunctionArgs) {
  const { shop } = await requireShopAndItsSeller(actionArgs.request)
  const product = await requireProduct(shop, actionArgs)

  if (actionArgs.request.method === 'PATCH') {
    return formAction({
      request: actionArgs.request,
      schema: patchProductSchema,
      mutation: patchProductMutation,
      environment: {
        shopId: shop.id,
        productId: product.id,
      } as UpdateProductEnv,
    })
  } else if (actionArgs.request.method === 'POST') {
    const formData = await actionArgs.request.clone().formData()
    const intent = formData.get('_intent')
    setActionFromIntent(intent)

    if (intent === 'archiveProduct') {
      return formAction({
        request: actionArgs.request,
        schema: archiveProductSchema,
        mutation: archiveProductMutation,
        environment: {
          shopId: shop.id,
          productId: product.id,
        } as UpdateProductEnv,
      })
    } else if (intent === 'restoreProduct') {
      return formAction({
        request: actionArgs.request,
        schema: restoreProductSchema,
        mutation: restoreProductMutation,
        environment: {
          shopId: shop.id,
          productId: product.id,
        } as UpdateProductEnv,
      })
    }
  } else {
    throw new Response('Method not allowed', { status: 405 })
  }

  throw new Response('Not implemented', { status: 400 })
}

export default function ProductViewPage() {
  const { product, buyerCountry, collections, productSeoData } =
    useLoaderData<typeof loader>()

  return (
    <>
      <Seo seoData={productSeoData} />
      <InnerPageTopNav />
      <ProductPage
        product={product}
        collections={collections}
        buyerCountry={buyerCountry}
      />
    </>
  )
}
